import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Package, AlertTriangle, Wrench, StickyNote, Trash2, PlusCircle } from "lucide-react";
import { adminEquipmentDetail, adminAddEquipmentLog, adminDeleteEquipmentLog, apiError } from "../../lib/api";
import { Field, Textarea, Select, Btn, StatusPill } from "./AdminKit";

const logIcon = { damage: AlertTriangle, maintenance: Wrench, repair: Wrench, note: StickyNote };
const logColor = { damage: "text-red-500 bg-red-50", maintenance: "text-amber-600 bg-amber-50", repair: "text-amber-600 bg-amber-50", note: "text-nead-navy/50 bg-black/5" };

function fmt(dt) { try { return new Date(dt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return dt; } }

export default function AdminEquipmentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [logForm, setLogForm] = useState({ type: "note", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => adminEquipmentDetail(id).then(setData).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);

  const addLog = async (e) => {
    e.preventDefault();
    if (!logForm.note.trim()) return;
    setBusy(true);
    try { await adminAddEquipmentLog(id, logForm); toast.success("Log entry added."); setLogForm({ type: "note", note: "" }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const delLog = async (logId) => {
    if (!window.confirm("Delete this log entry?")) return;
    try { await adminDeleteEquipmentLog(id, logId); load(); } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return <div className="text-nead-navy/40">Loading…</div>;
  const { item, current, out_quantity, available, history, logs } = data;

  return (
    <div>
      <Link to="/admin/equipment" className="inline-flex items-center gap-2 text-sm text-nead-navy/55 hover:text-nead-pink mb-5"><ArrowLeft className="h-4 w-4" /> Equipment</Link>

      <div className="flex flex-wrap items-start gap-6">
        {item.image && <div className="h-28 w-40 flex-none overflow-hidden rounded-xl bg-nead-mist"><img src={item.image} alt={item.name} className="h-full w-full object-cover" /></div>}
        <div>
          <h1 className="font-display font-bold text-2xl text-nead-navy" data-testid="equipment-detail-name">{item.name}</h1>
          <div className="mt-1 text-sm text-nead-navy/55">{item.category} · £{Number(item.day_rate).toFixed(2)}/day</div>
          {item.description && <p className="mt-2 text-sm text-nead-navy/60 max-w-xl">{item.description}</p>}
        </div>
      </div>

      {/* Status cards */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Package, label: "Total stock", value: item.quantity },
          { icon: Package, label: "Currently out", value: out_quantity, highlight: out_quantity > 0 },
          { icon: Package, label: "Available", value: available },
        ].map((c) => (
          <div key={c.label} className={`rounded-2xl border p-5 bg-white ${c.highlight ? "border-nead-pink/40" : "border-black/[0.07]"}`}>
            <div className="text-3xl font-display font-bold text-nead-navy tabular-nums">{c.value}</div>
            <div className="mt-1 text-sm text-nead-navy/55">{c.label}</div>
          </div>
        ))}
        <div className="rounded-2xl border border-black/[0.07] p-5 bg-white">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-nead-navy"><MapPin className="h-4 w-4 text-nead-pink" />{current ? "Out now" : "In store"}</div>
          <div className="mt-1 text-sm text-nead-navy/55">{current ? (current.location || current.customer_name || "On hire") : "At NEAD"}</div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Hire history */}
        <div className="rounded-2xl border border-black/[0.07] bg-white p-6">
          <h2 className="font-display font-bold text-lg text-nead-navy">Hire history</h2>
          <div className="mt-5 space-y-4">
            {history.map((h) => (
              <div key={h.booking_id} data-testid={`history-${h.booking_id}`} className="flex items-start gap-3 border-l-2 border-black/10 pl-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-nead-navy text-sm">{h.customer_name}</span>
                    <StatusPill status={h.status} />
                  </div>
                  <div className="mt-0.5 text-xs text-nead-navy/55">{h.quantity}× · {h.date_from || "?"} to {h.date_to || "?"}</div>
                  {h.location && <div className="mt-0.5 flex items-center gap-1 text-xs text-nead-navy/55"><MapPin className="h-3 w-3" />{h.location}</div>}
                  {h.notes && <div className="mt-1 text-xs text-nead-navy/50 italic">{h.notes}</div>}
                </div>
              </div>
            ))}
            {!history.length && <p className="text-sm text-nead-navy/40">No hire history yet.</p>}
          </div>
        </div>

        {/* Condition & damage log */}
        <div className="rounded-2xl border border-black/[0.07] bg-white p-6">
          <h2 className="font-display font-bold text-lg text-nead-navy">Condition &amp; damage log</h2>
          <form onSubmit={addLog} className="mt-4 space-y-3" data-testid="log-form">
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <Field label="Type">
                <Select value={logForm.type} onChange={(e) => setLogForm((f) => ({ ...f, type: e.target.value }))} data-testid="log-type">
                  <option value="note">Note</option>
                  <option value="damage">Damage</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="repair">Repair</option>
                </Select>
              </Field>
              <Field label="Entry"><Textarea rows={1} value={logForm.note} onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. Scuffed corner, needs new leg" data-testid="log-note" /></Field>
            </div>
            <Btn type="submit" disabled={busy} data-testid="log-add"><PlusCircle className="h-4 w-4" /> Add entry</Btn>
          </form>
          <div className="mt-6 space-y-3">
            {(logs || []).slice().reverse().map((l) => {
              const Icon = logIcon[l.type] || StickyNote;
              return (
                <div key={l.id} data-testid={`log-${l.id}`} className="flex items-start gap-3">
                  <div className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg ${logColor[l.type] || logColor.note}`}><Icon className="h-4 w-4" /></div>
                  <div className="flex-1">
                    <div className="text-sm text-nead-navy">{l.note}</div>
                    <div className="text-xs text-nead-navy/45">{l.type} · {l.author} · {fmt(l.created_at)}</div>
                  </div>
                  <button onClick={() => delLog(l.id)} className="text-nead-navy/30 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
            {!(logs || []).length && <p className="text-sm text-nead-navy/40">No log entries yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
